import { useEffect, useState } from "react";
import { useParams } from "react-router";
import ResponsiveTable, { Column } from "@/components/Usados/ResponsiveTable";
import ActionMenu from "@/components/Usados/ActionMenu";
import Badge from "@/components/ui/badge/Badge";
import Button from "@/components/ui/button/Button";
import { Modal } from "@/components/Usados/modal";
import { Pencil, Trash, Eye } from "lucide-react";
import Input from "@/components/Usados/InputField";
import Label from "@/components/Usados/Label";
import { apiFetch } from "@/service/api";

export default function ListarUsuarioTab({ onEdit }: any) {
  const { id: proyecto_id } = useParams();
  const api = import.meta.env.VITE_API_URL_BACKEND;
  const [data, setData] = useState<any[]>([]);
  const [avances, setAvances] = useState<any[]>([]);
  const [selected, setSelected] = useState<any>(null);
  const [showAvances, setShowAvances] = useState(false);
  const [formAvance, setFormAvance] = useState<any>({
    descripcion: "",
    porcentaje: 0,
  });
  const [file, setFile] = useState<File | null>(null);

  const fetchData = async () => {
    const res = await apiFetch(
      `empleados-proyectos/find-proyecto/${proyecto_id}`,
    );
    if (res.ok) setData(await res.json());
  };
  useEffect(() => {
    fetchData();
  }, [proyecto_id]);

  const fetchAvances = async (tarea: any) => {
    setSelected(tarea);
    const res = await apiFetch(`avances-tareas/tarea/${tarea.id}`);
    if (res.ok) setAvances(await res.json());
    setShowAvances(true);
  };

  const guardarAvance = async () => {
    const res = await apiFetch(`avances-tareas/create`, {
      method: "POST",
      body: JSON.stringify({
        tarea_id: selected.id,
        fecha_programada: new Date().toISOString().split("T")[0],
        fecha_real: new Date().toISOString().split("T")[0],
        descripcion: formAvance.descripcion,
        porcentaje: Number(formAvance.porcentaje),
      }),
    });
    if (res.ok) {
      const nuevo = await res.json();
      if (file) {
        const fd2 = new FormData();
        fd2.append("file", file);
        await apiFetch(`avances-tareas/upload-soporte/${nuevo.id}`, {
          method: "POST",
          body: fd2,
        });
      }
      const res2 = await apiFetch(`avances-tareas/tarea/${selected.id}`);
      if (res2.ok) setAvances(await res2.json());
      fetchData();
      setFormAvance({ descripcion: "", porcentaje: 0 });
      setFile(null);
    }
  };

  const getSoporteUrl = (path: string) => {
    if (!path) return null;
    if (path.startsWith("http")) return path;
    if (path.startsWith("/")) return `${api.replace("/api", "")}${path}`;
    return path;
  };

  const columns: Column<any>[] = [
    { label: "Tarea", key: "tarea" },
    {
      label: "Empleado",
      key: "empleado",
      render: (v: any) => (v?.nombres ? `${v.nombres} ${v.apellidos}` : "-"),
    },
    {
      label: "Fechas",
      key: "fecha_inicio",
      render: (_: any, row: any) =>
        `${row.fecha_inicio?.split("T")[0] || ""} → ${
          row.fecha_fin?.split("T")[0] || ""
        }`,
    },
    {
      label: "Frecuencia",
      key: "frecuencia_avance",
      render: (v: any) => <Badge color="light">{v}</Badge>,
    },
    {
      label: "Progreso",
      key: "progreso",
      render: (v: any) => (
        <div className="w-20">
          <div className="text-">{v || 0}%</div>
          <div className="w-full bg-gray-200 h-1.5 rounded-full">
            <div
              className="bg-blue-500 h-1.5 rounded-full"
              style={{ width: `${v || 0}%` }}
            ></div>
          </div>
        </div>
      ),
    },
    {
      label: "Soporte",
      key: "requiere_soporte",
      render: (v: any) =>
        v ? (
          <span className="text-xs bg-amber-100 px-2 py-1 rounded">
            Obligatorio
          </span>
        ) : (
          <span className="text-xs text-gray-400">Opcional</span>
        ),
    },
    {
      label: "Acciones",
      key: "id",
      render: (_: any, row: any) => (
        <ActionMenu
          actions={[
            {
              label: "Ver avances",
              icon: <Eye className="size-4" />,
              onClick: () => fetchAvances(row),
            },
            {
              label: "Editar",
              icon: <Pencil className="size-4" />,
              onClick: () => onEdit(row),
            },
            {
              label: "Eliminar",
              icon: <Trash className="size-4" />,
              onClick: async () => {
                await apiFetch(`empleados-proyectos/remove/${row.id}`, {
                  method: "DELETE",
                });
                fetchData();
              },
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div>
      <ResponsiveTable columns={columns} data={data} />
      <Modal
        isOpen={showAvances}
        onClose={() => setShowAvances(false)}
        className="max-w-3xl"
      >
        <div className="p-6 space-y-6">
          <h4 className="font-semibold">Avances de: {selected?.tarea}</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 border rounded-xl p-4 bg-gray-50">
            <div className="col-span-2">
              <Label>Descripción avance</Label>
              <Input
                value={formAvance.descripcion}
                onChange={(e: any) =>
                  setFormAvance({ ...formAvance, descripcion: e.target.value })
                }
                placeholder="Que se hizo hoy"
              />
            </div>
            <div>
              <Label>% Avance</Label>
              <Input
                type="number"
                value={formAvance.porcentaje}
                onChange={(e: any) =>
                  setFormAvance({ ...formAvance, porcentaje: e.target.value })
                }
              />
            </div>
            <div className="col-span-3">
              <Label>Foto / Documento (obligatorio si requiere soporte)</Label>
              <input
                type="file"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="w-full border rounded-lg p-2 text-sm"
              />
            </div>
            <div className="col-span-3">
              <Button onClick={guardarAvance}>Guardar avance</Button>
            </div>
          </div>
          <div className="border rounded-xl overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-2 text-left">Fecha Prog.</th>
                  <th className="p-2">Fecha Real</th>
                  <th className="p-2">%</th>
                  <th className="p-2">Descripción</th>
                  <th className="p-2">Soporte</th>
                </tr>
              </thead>
              <tbody>
                {avances.map((a) => (
                  <tr key={a.id} className="border-t">
                    <td className="p-2">{a.fecha_programada?.split("T")[0]}</td>
                    <td className="p-2">
                      {a.fecha_real?.split("T")[0] || "-"}
                    </td>
                    <td className="p-2">{a.porcentaje}%</td>
                    <td className="p-2">{a.descripcion || "-"}</td>
                    <td className="p-2">
                      {a.soporte_url ? (
                        <a
                          href={getSoporteUrl(a.soporte_url)!}
                          target="_blank"
                          className="text-blue-600"
                        >
                          Ver
                        </a>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                ))}
                {avances.length === 0 && (
                  <tr>
                    <td colSpan={5} className="p-6 text-center text-gray-400">
                      Sin avances programados
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Modal>
    </div>
  );
}
